import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Undo2, Trash2, Check, Pen, Circle, ArrowUpRight, Type as TypeIcon } from "lucide-react";

/**
 * PhotoAnnotator — canvas-based photo editor.
 *
 * Props:
 *  - src (string, required): image URL (blob: or remote)
 *  - onSave: (Blob) => void  — called with the annotated JPEG blob
 *  - onCancel: () => void
 *
 * Tools:
 *  - pen (free draw)
 *  - arrow (click-drag)
 *  - circle (click-drag)
 *  - text (click, prompts for label)
 * Colors: red (#ff3b30), yellow (#ffcc00), green (#10b981).
 *
 * Rendered inside a full-screen modal overlay.
 */
const COLORS = [
  { value: "#ff3b30", label: "Rosso · problema" },
  { value: "#ffcc00", label: "Giallo · attenzione" },
  { value: "#10b981", label: "Verde · OK" },
];
const TOOLS = [
  { id: "pen", label: "Pennarello", Icon: Pen },
  { id: "arrow", label: "Freccia", Icon: ArrowUpRight },
  { id: "circle", label: "Cerchio", Icon: Circle },
  { id: "text", label: "Testo", Icon: TypeIcon },
];

export default function PhotoAnnotator({ src, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#ff3b30");
  const [shapes, setShapes] = useState([]); // committed shapes
  const drawingRef = useRef(null); // in-progress shape
  const [, force] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dims, setDims] = useState(null); // {w, h}

  // Load image (decoupled from canvas mounting)
  useEffect(() => {
    setLoadError("");
    setReady(false);
    setDims(null);
    if (!src) {
      setLoadError("Foto non disponibile.");
      return;
    }
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const max = 2000;
      const { naturalWidth: w, naturalHeight: h } = img;
      const ratio = w > max || h > max ? (w >= h ? max / w : max / h) : 1;
      setDims({ w: Math.round(w * ratio), h: Math.round(h * ratio) });
    };
    img.onerror = () => {
      setLoadError("Impossibile caricare la foto. Verifica la connessione e riprova.");
    };
    img.src = src;
  }, [src]);

  // When dims become available AND canvas is mounted, size it and mark ready
  useEffect(() => {
    if (!dims) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = dims.w;
    canvas.height = dims.h;
    setReady(true);
  }, [dims]);

  // Draw everything on every state change
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    // brush scale — match line thickness to canvas size
    const scale = Math.max(2, Math.round(canvas.width / 500));
    const all = drawingRef.current ? [...shapes, drawingRef.current] : shapes;
    all.forEach((s) => drawShape(ctx, s, scale));
  });

  const canvasCoords = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const e = evt.touches ? evt.touches[0] : evt;
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    return { x, y };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    const p = canvasCoords(e);
    if (tool === "text") {
      const label = window.prompt("Testo:");
      if (label) {
        setShapes((s) => [...s, { type: "text", color, x: p.x, y: p.y, text: label }]);
      }
      return;
    }
    if (tool === "pen") {
      drawingRef.current = { type: "pen", color, points: [p] };
    } else if (tool === "arrow") {
      drawingRef.current = { type: "arrow", color, x0: p.x, y0: p.y, x1: p.x, y1: p.y };
    } else if (tool === "circle") {
      drawingRef.current = { type: "circle", color, x0: p.x, y0: p.y, x1: p.x, y1: p.y };
    }
    force((n) => n + 1);
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = canvasCoords(e);
    const d = drawingRef.current;
    if (d.type === "pen") d.points.push(p);
    else {
      d.x1 = p.x; d.y1 = p.y;
    }
    force((n) => n + 1);
  };

  const onPointerUp = () => {
    const d = drawingRef.current;
    if (d) {
      setShapes((s) => [...s, d]);
      drawingRef.current = null;
      force((n) => n + 1);
    }
  };

  const undo = () => setShapes((s) => s.slice(0, -1));
  const clear = () => setShapes([]);

  const save = async () => {
    if (!canvasRef.current) return;
    setSaving(true);
    const blob = await new Promise((resolve) =>
      canvasRef.current.toBlob(resolve, "image/jpeg", 0.9),
    );
    setSaving(false);
    if (blob && onSave) onSave(blob);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col" data-testid="photo-annotator">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black text-white">
        <button onClick={onCancel} className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100"
                data-testid="annotator-cancel">
          <X className="w-5 h-5" /> Annulla
        </button>
        <div className="text-xs uppercase tracking-widest opacity-70">Modifica foto</div>
        <button onClick={save} disabled={saving || !ready}
                className="flex items-center gap-2 bg-[#ff5a00] hover:bg-[#ff7a30] disabled:opacity-50
                           text-white text-sm font-bold px-4 py-1.5 rounded-full"
                data-testid="annotator-save">
          <Check className="w-4 h-4" /> {saving ? "Salvo…" : "Salva"}
        </button>
      </div>

      {/* Canvas */}
      <div ref={containerRef}
           className="flex-1 flex items-center justify-center overflow-hidden p-2 sm:p-6 touch-none relative">
        {loadError && (
          <div className="text-center text-white/80 text-sm max-w-sm" data-testid="annotator-error">
            <div className="mb-3 text-[#ff3b30] text-base font-bold">Errore caricamento</div>
            <div>{loadError}</div>
            <button onClick={onCancel} className="mt-4 bru-btn bru-btn-secondary">Chiudi</button>
          </div>
        )}
        {!loadError && !ready && (
          <div className="absolute inset-0 flex items-center justify-center text-white opacity-50 text-sm pointer-events-none">
            Caricamento…
          </div>
        )}
        {!loadError && (
          <canvas
            ref={canvasRef}
            className={`max-w-full max-h-full rounded-lg shadow-2xl bg-black ${ready ? "cursor-crosshair" : "invisible"}`}
            onMouseDown={onPointerDown} onMouseMove={onPointerMove} onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
            onTouchStart={onPointerDown} onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
            data-testid="annotator-canvas"
          />
        )}
      </div>

      {/* Bottom toolbar */}
      <div className="px-3 py-3 bg-black border-t border-neutral-800 flex flex-wrap items-center justify-between gap-3">
        {/* Tools */}
        <div className="flex gap-1.5" data-testid="annotator-tools">
          {TOOLS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTool(id)}
              title={label}
              className={`w-11 h-11 rounded-2xl flex items-center justify-center transition
                         ${tool === id ? "bg-white text-black" : "bg-neutral-800 text-white/80 hover:bg-neutral-700"}`}
              data-testid={`annotator-tool-${id}`}
            >
              <Icon className="w-5 h-5" />
            </button>
          ))}
        </div>
        {/* Colors */}
        <div className="flex gap-2" data-testid="annotator-colors">
          {COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setColor(c.value)}
              title={c.label}
              className={`w-10 h-10 rounded-full transition border-[3px]
                         ${color === c.value ? "border-white" : "border-transparent"}`}
              style={{ background: c.value }}
              data-testid={`annotator-color-${c.value.slice(1)}`}
            />
          ))}
        </div>
        {/* Actions */}
        <div className="flex gap-1.5">
          <button type="button" onClick={undo} title="Annulla ultimo"
                  className="w-11 h-11 rounded-2xl bg-neutral-800 text-white/80 hover:bg-neutral-700 flex items-center justify-center"
                  data-testid="annotator-undo">
            <Undo2 className="w-5 h-5" />
          </button>
          <button type="button" onClick={clear} title="Pulisci tutto"
                  className="w-11 h-11 rounded-2xl bg-neutral-800 text-white/80 hover:bg-neutral-700 flex items-center justify-center"
                  data-testid="annotator-clear">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// --- Shape drawing helpers ---
function drawShape(ctx, s, scale) {
  ctx.save();
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = 3 * scale;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (s.type === "pen") {
    ctx.beginPath();
    s.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
  } else if (s.type === "arrow") {
    drawArrow(ctx, s.x0, s.y0, s.x1, s.y1, 3 * scale);
  } else if (s.type === "circle") {
    const cx = (s.x0 + s.x1) / 2;
    const cy = (s.y0 + s.y1) / 2;
    const rx = Math.abs(s.x1 - s.x0) / 2;
    const ry = Math.abs(s.y1 - s.y0) / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.max(5, rx), Math.max(5, ry), 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.type === "text") {
    const fs = 20 * scale;
    ctx.font = `bold ${fs}px -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial`;
    // backdrop rect for legibility
    const tw = ctx.measureText(s.text).width;
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(s.x - 4, s.y - fs, tw + 8, fs + 6);
    ctx.fillStyle = s.color;
    ctx.fillText(s.text, s.x, s.y);
  }
  ctx.restore();
}

function drawArrow(ctx, x1, y1, x2, y2, thickness) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  // arrowhead
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const head = Math.max(14, thickness * 5);
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - head * Math.cos(angle - Math.PI / 6),
    y2 - head * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    x2 - head * Math.cos(angle + Math.PI / 6),
    y2 - head * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();
}
